import type { RefObject } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Scroll budget of the section, expressed in viewport heights.
 *
 * The section is a tall wrapper holding a `position: sticky`, viewport-tall
 * stage. A sticky child stays pinned for `wrapperHeight - viewportHeight` of
 * scrolling, so one extra viewport has to be added on top of the phases below
 * to give every phase its full range.
 *
 * Nothing is scrubbed by the scroll — these only decide *when* the video takes
 * over the screen and which slide is showing. Both animations then play out on
 * their own clock.
 */
export const EXPAND_VIEWPORTS = 0.35
/**
 * Runway for the video to drop back into its container. Unlike the expand
 * phase this is not a lead-in — the collapse fires as soon as the last slide
 * has had its turn, and this is the pinned scroll it animates over. Too little
 * and the section unpins mid-collapse, leaving the reader looking at a
 * half-shrunk video already scrolling away.
 */
export const COLLAPSE_VIEWPORTS = 0.5
/**
 * How far the reader scrolls to move on one slide. A whole viewport per slide
 * reads as a long wait — roughly seven wheel notches before anything happens —
 * so a slide turns over in half a screen.
 */
export const SLIDE_VIEWPORTS = 0.5

/**
 * Deadband around every switch point, in viewport heights. Both thresholds are
 * crossings of a continuous scroll value, so without it a couple of pixels of
 * wheel or trackpad jitter flips the target back and forth and the animation
 * restarts each way — which reads as the content shaking. Costs a little
 * lateness at the switch and buys immunity to jitter far larger than any real
 * input noise.
 */
export const SWITCH_HYSTERESIS_VIEWPORTS = 0.08

/** How long one slide switch takes once the scroll has triggered it. */
export const SLIDE_SWITCH_MS = 600
/** How long the video takes to fill the screen, or to drop back into place. */
export const VIDEO_EXPAND_MS = 400

/**
 * Custom property carrying how far the video has grown out of its container
 * towards full bleed: `0` — contained, `1` — edge to edge. It only ever rests on
 * those two values; the scroll flips the target and the tween covers the ground
 * in between. The stage geometry is expressed in CSS against this single value,
 * so a frame only sets one property on the wrapper instead of re-rendering the
 * whole section.
 */
export const PROGRESS_PROPERTY = "--full-video-progress"

/**
 * Custom property carrying the slider position. It only ever rests on whole
 * numbers — one slide, centred — and passes through the fractions in between
 * while a switch plays out. Each slide derives its own offset from it in CSS
 * (`position - ownIndex`).
 */
export const SLIDE_POSITION_PROPERTY = "--full-video-slide-position"

/**
 * Scroll offset, in viewport heights, at which `index` is the showing slide —
 * the inverse of the reading in `update`, which turns the offset back into a
 * slide number.
 */
export function getSlideScrollViewports(index: number): number {
  return EXPAND_VIEWPORTS + index * SLIDE_VIEWPORTS
}

export function getWrapperHeightVh(slidesCount: number): number {
  return (
    (EXPAND_VIEWPORTS +
      slidesCount * SLIDE_VIEWPORTS +
      COLLAPSE_VIEWPORTS +
      1) *
    100
  )
}

export type FullVideoSliderScroll = {
  wrapperRef: RefObject<HTMLDivElement>
  activeIndex: number
  isFullScreen: boolean
  /**
   * Puts the scroll where `index` is the showing slide. The slides are driven
   * by scroll position and nothing else, so this is how anything other than a
   * wheel — a keyboard, say — gets to move between them.
   */
  goToSlide: (index: number) => void
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const easeOutCubic = (progress: number): number => 1 - (1 - progress) ** 3

/**
 * Which slide the scroll is asking for. The current slide keeps the target until
 * the scroll is clear of the halfway mark by the deadband — sitting exactly on
 * that mark otherwise flips the target on every stray pixel of wheel jitter,
 * restarting the switch each way, which reads as the content shaking. A jump
 * far past the mark still snaps straight to wherever it landed.
 */
export function getNextSlideIndex(
  rawSlide: number,
  currentTarget: number,
  slidesCount: number
): number {
  // `rawSlide` counts slides, so the deadband has to be converted out of
  // viewport heights — otherwise shortening a slide's range would quietly
  // widen it and the switch would drift late.
  const deadband = 0.5 + SWITCH_HYSTERESIS_VIEWPORTS / SLIDE_VIEWPORTS

  if (Math.abs(rawSlide - currentTarget) <= deadband) {
    return currentTarget
  }

  return clamp(Math.round(rawSlide), 0, slidesCount - 1)
}

/**
 * Whether the video should be covering the screen. The band widens once it is,
 * so dropping back out takes a deliberate scroll rather than a pixel of jitter
 * at the threshold — the same shake, on the way in and out of full screen.
 */
export function getShouldFillScreen(
  scrolledViewports: number,
  slidesCount: number,
  isFilling: boolean
): boolean {
  const margin = isFilling
    ? -SWITCH_HYSTERESIS_VIEWPORTS
    : SWITCH_HYSTERESIS_VIEWPORTS
  const lastSlideEnd = EXPAND_VIEWPORTS + slidesCount * SLIDE_VIEWPORTS

  return (
    // The expand waits until the reader is committed — half its phase in. The
    // collapse does not: it starts the moment the last slide's turn is over, so
    // the whole collapse phase is spent animating rather than sitting still on
    // a slide that has already finished.
    scrolledViewports >= EXPAND_VIEWPORTS / 2 + margin &&
    scrolledViewports <= lastSlideEnd - margin
  )
}

/**
 * A number the scroll re-targets and a tween carries there — the shape both the
 * video expansion and the slider position take.
 */
type AnimatedValue = {
  value: number
  target: number
  tween: { from: number; startedAt: number } | null
}

const createAnimatedValue = (): AnimatedValue => ({
  value: 0,
  target: 0,
  tween: null,
})

/**
 * Points an animated value at a new target. `immediate` skips the tween, for
 * the first frame — landing mid-section on a reload should show that state
 * straight away rather than animating into it from the top.
 */
function retarget(
  animated: AnimatedValue,
  target: number,
  now: number,
  immediate: boolean
): void {
  if (animated.target === target) {
    return
  }

  animated.target = target

  if (immediate) {
    animated.value = target
    animated.tween = null

    return
  }

  animated.tween = { from: animated.value, startedAt: now }
}

/** Advances a tween by one frame. Returns whether it still has ground to cover. */
function advance(
  animated: AnimatedValue,
  now: number,
  durationMs: number
): boolean {
  if (!animated.tween) {
    return false
  }

  const elapsed = clamp((now - animated.tween.startedAt) / durationMs, 0, 1)

  animated.value =
    animated.tween.from +
    (animated.target - animated.tween.from) * easeOutCubic(elapsed)

  if (elapsed >= 1) {
    animated.value = animated.target
    animated.tween = null
  }

  return animated.tween !== null
}

/**
 * Drives the section from the scroll position.
 *
 * Nothing here advances on its own — there is no timer, and the section sits
 * still whenever the reader does. What the scroll does *not* do is scrub the
 * animations frame by frame: crossing a threshold picks the new state, and the
 * move to it then runs for a fixed `VIDEO_EXPAND_MS` / `SLIDE_SWITCH_MS`, the
 * way a CSS transition would. So the motion looks the same whether the reader
 * creeps or flings the wheel. Scrolling back picks the previous state and the
 * same move runs in reverse.
 *
 * All reads happen inside a `requestAnimationFrame` callback so a scroll burst
 * never triggers more than one layout read per frame, and only the two values
 * that change rarely — the active slide and whether the video covers the screen
 * — are kept in React state.
 */
export function useFullVideoSliderScroll(
  slidesCount: number
): FullVideoSliderScroll {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isFullScreen, setIsFullScreen] = useState(false)

  useEffect(() => {
    const wrapper = wrapperRef.current

    if (!wrapper || slidesCount === 0) {
      return
    }

    let rafId: number | undefined
    let hasSettledOnce = false
    const expansion = createAnimatedValue()
    const slidePosition = createAnimatedValue()

    /** Returns whether an animation is still in flight and needs another frame. */
    const update = (now: number): boolean => {
      const viewportHeight = window.innerHeight
      const scrollable = wrapper.offsetHeight - viewportHeight

      if (scrollable <= 0) {
        return false
      }

      const scrolled = clamp(
        -wrapper.getBoundingClientRect().top,
        0,
        scrollable
      )
      const scrolledViewports = scrolled / viewportHeight

      const shouldFillScreen = getShouldFillScreen(
        scrolledViewports,
        slidesCount,
        expansion.target === 1
      )
      const nextSlide = getNextSlideIndex(
        (scrolledViewports - EXPAND_VIEWPORTS) / SLIDE_VIEWPORTS,
        slidePosition.target,
        slidesCount
      )

      retarget(expansion, shouldFillScreen ? 1 : 0, now, !hasSettledOnce)
      retarget(slidePosition, nextSlide, now, !hasSettledOnce)
      hasSettledOnce = true

      const expanding = advance(expansion, now, VIDEO_EXPAND_MS)
      const switching = advance(slidePosition, now, SLIDE_SWITCH_MS)

      wrapper.style.setProperty(PROGRESS_PROPERTY, `${expansion.value}`)
      wrapper.style.setProperty(
        SLIDE_POSITION_PROPERTY,
        `${slidePosition.value}`
      )

      // Follows the target, not the tween: the sticky page header has to be out
      // of the way for the whole expansion, not just once it finishes, and the
      // header reacts to this the moment it is committed — which may be after
      // the reader has already stopped scrolling.
      setIsFullScreen(shouldFillScreen)
      setActiveIndex(clamp(Math.round(slidePosition.value), 0, slidesCount - 1))

      return expanding || switching
    }

    const frame = (now: number): void => {
      rafId = undefined

      if (update(now)) {
        schedule()
      }
    }

    const schedule = (): void => {
      if (rafId === undefined) {
        rafId = requestAnimationFrame(frame)
      }
    }

    update(performance.now())

    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule, { passive: true })

    return () => {
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)

      if (rafId !== undefined) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [slidesCount])

  const goToSlide = useCallback((index: number): void => {
    const wrapper = wrapperRef.current

    if (!wrapper) {
      return
    }

    const viewportHeight = window.innerHeight
    const scrollable = wrapper.offsetHeight - viewportHeight

    if (scrollable <= 0) {
      return
    }

    // Same arithmetic as `update`, run backwards: it reads the wrapper's offset
    // from the top of the viewport to decide the slide, so landing on a slide
    // means putting that offset where the slide wants it.
    const scrolled = clamp(
      getSlideScrollViewports(index) * viewportHeight,
      0,
      scrollable
    )

    window.scrollTo({
      top: window.scrollY + wrapper.getBoundingClientRect().top + scrolled,
    })
  }, [])

  return {
    wrapperRef,
    activeIndex,
    isFullScreen,
    goToSlide,
  }
}
